/**
 * Utility class to manage pending async actions that should execute
 * only after a prerequisite Promise resolves
 */
export class PendingAsyncActions<T = void> {
  private prerequisitePromise: Promise<T>
  private pendingActions: Array<{
    action: () => Promise<any>
    resolve: (value: any) => void
    reject: (reason: any) => void
  }> = []
  private isPrerequisiteFulfilled = false
  private prerequisiteResult: T | undefined
  private prerequisiteError: any

  /**
   * Create a new PendingAsyncActions instance
   * @param prerequisitePromise The promise that must resolve before pending actions can execute
   */
  constructor(prerequisitePromise: Promise<T>) {
    this.prerequisitePromise = prerequisitePromise

    // Handle the prerequisite promise
    this.prerequisitePromise
      .then((result) => {
        this.isPrerequisiteFulfilled = true
        this.prerequisiteResult = result
        this.executePendingActions()
      })
      .catch((error) => {
        this.isPrerequisiteFulfilled = true
        this.prerequisiteError = error
        this.rejectPendingActions(error)
      })
  }

  /**
   * Add an action to be executed after the prerequisite promise resolves
   * @param action Function that returns a promise to be executed
   * @returns Promise that resolves/rejects when the action completes
   */
  public addAction<R>(action: () => Promise<R>): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      if (this.isPrerequisiteFulfilled) {
        if (this.prerequisiteError) {
          // If prerequisite failed, immediately reject
          reject(this.prerequisiteError)
        } else {
          // If prerequisite fulfilled, immediately execute
          action().then(resolve).catch(reject)
        }
      } else {
        // Otherwise queue the action
        this.pendingActions.push({
          action,
          resolve,
          reject,
        })
      }
    })
  }

  /**
   * Execute all pending actions
   */
  private executePendingActions(): void {
    this.pendingActions.forEach(({ action, resolve, reject }) => {
      action().then(resolve).catch(reject)
    })
    this.pendingActions = []
  }

  /**
   * Reject all pending actions with the given error
   */
  private rejectPendingActions(error: any): void {
    this.pendingActions.forEach(({ reject }) => {
      reject(error)
    })
    this.pendingActions = []
  }

  /**
   * Check if the prerequisite promise has been fulfilled (resolved or rejected)
   */
  public isReady(): boolean {
    return this.isPrerequisiteFulfilled
  }

  /**
   * Get the result of the prerequisite promise if fulfilled
   */
  public getPrerequisiteResult(): T | undefined {
    return this.prerequisiteResult
  }
}
